import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  Grid,
  Card,
  CardContent,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { styled } from '@mui/material/styles';

import productService from '../../services/productService';
import purchaseOrderService from '../../services/purchaseOrderService';
import { supplierService } from '../../services/supplierService';
import axiosInstance from '../../utils/axios';
import { useNavigate } from 'react-router-dom';
import ProductFormEnhanced from '../products/ProductFormEnhanced';
import { PersonAdd as PersonAddIcon, Search as SearchIcon } from '@mui/icons-material';
import { getCurrentUser } from '../../services/authService';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '8px 12px',
  border: '1px solid #e0e0e0',
  fontSize: '15px',
  '&.focused': {
    backgroundColor: '#e3f2fd',
    outline: `2px solid ${theme.palette.primary.main}`,
  },
}));

const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  padding: '10px 12px',
  border: '1px solid #bdc3c7',
  backgroundColor: '#2c3e50',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '15px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  lineHeight: 1.2,
}));

const FunctionKeyBar = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderTop: '2px solid #3498db',
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '8px',
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1200,
}));

const FunctionKey = styled(Button)(({ theme }) => ({
  minWidth: '100px',
  padding: '8px 12px',
  backgroundColor: '#ecf0f1',
  color: '#2c3e50',
  fontSize: '13px',
  fontWeight: '600',
  textTransform: 'none',
  border: '1px solid #bdc3c7',
  '&:hover': {
    backgroundColor: '#bdc3c7',
  },
  '& .key': {
    fontWeight: 'bold',
    color: '#3498db',
    fontSize: '14px',
    marginRight: '4px',
  },
}));

const POKeyboard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Refs for input cells
  const itemCodeRefs = useRef([]);
  const qtyRefs = useRef([]);
  const priceRefs = useRef([]);
  const sellingPriceRefs = useRef([]);
  const profitMarginRefs = useRef([]);
  const deleteYesRef = useRef(null);

  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const [selectedTax, setSelectedTax] = useState(null);
  const [taxRates, setTaxRates] = useState([]);

  // State
  const [cart, setCart] = useState([
    { id: 1, itemCode: '', productName: '', quantity: 1, price: 0, sellingPrice: 0, profitMargin: 0, tax: selectedTax, taxAmount: 0, totalAmount: 0, discount: 0, discountPercentage: 0, discountAmount: 0, product: null },
  ]);
  const [supplier, setSupplier] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [supplierInputValue, setSupplierInputValue] = useState('');
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [receivedAmount, setReceivedAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [discount, setDiscount] = useState(0);
  const [searchingProduct, setSearchingProduct] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [productOptions, setProductOptions] = useState({});
  const [showProductDropdown, setShowProductDropdown] = useState({});
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [highlightIndexByRow, setHighlightIndexByRow] = useState({});
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [productNotFoundDialogOpen, setProductNotFoundDialogOpen] = useState(false);
  const [productCreateDialogOpen, setProductCreateDialogOpen] = useState(false);
  const [pendingProductCode, setPendingProductCode] = useState('');
  const [pendingRowIndex, setPendingRowIndex] = useState(-1);
  const confirmButtonRef = useRef(null);
  const [addSupplierDialogOpen, setAddSupplierDialogOpen] = useState(false);
  const [supplierFormValues, setSupplierFormValues] = useState(() => {
    const user = getCurrentUser();
    return {
      name: '',
      contact_person: '',
      email: '',
      phone: '',
      website: '',
      address: '',
      notes: '',
      company_id: user?.companyId || 1
    };
  });
  const [supplierFormErrors, setSupplierFormErrors] = useState({});

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  // Compute per-unit purchase after tax (inclusive)
  const getPurchaseAfterTaxPerUnit = (item) => {
    const quantity = item.quantity || 0;
    if (quantity <= 0) return 0;
    const baseAmount = quantity * (item.price || 0);
    const discountAmount = item.discount || 0;
    const taxableAmount = baseAmount - discountAmount;
    const taxRate = item.tax?.rate || 0;
    const taxAmount = taxRate > 0 ? taxableAmount * (taxRate / 100) : 0;
    const totalAmount = baseAmount - discountAmount + taxAmount;
    return Math.round((totalAmount / quantity) * 100) / 100;
  };

  // Handle change of per-unit purchase after tax (inclusive value)
  const handlePurchaseAfterTaxChange = (rowIndex, newValue) => {
    if (newValue === '') {
      setCart(prev => prev.map((item, index) =>
        index === rowIndex ? { ...item, price: 0, taxAmount: 0, totalAmount: 0, profitMargin: 0 } : item
      ));
      return;
    }
    const cleanValue = String(newValue).replace(/^0+/, '');
    const inclusivePerUnit = Math.max(0, parseFloat(cleanValue) || 0);
    setCart(prev => prev.map((item, index) => {
      if (index !== rowIndex) return item;
      const quantity = item.quantity || 0;
      const taxRate = item.tax?.rate || 0;
      const exclusivePerUnit = taxRate > 0 ? (inclusivePerUnit / (1 + taxRate / 100)) : inclusivePerUnit;
      const baseAmount = exclusivePerUnit * quantity;
      const discountAmount = item.discount || 0;
      const taxableAmount = baseAmount - discountAmount;
      const taxAmount = taxRate > 0 ? taxableAmount * (taxRate / 100) : 0;
      const totalAmount = baseAmount - discountAmount + taxAmount;
      const sellingPrice = item.sellingPrice || 0;
      const profitMargin = inclusivePerUnit > 0 ? ((sellingPrice - inclusivePerUnit) / inclusivePerUnit * 100) : 0;
      return {
        ...item,
        price: Math.round(exclusivePerUnit * 100) / 100,
        taxAmount: Math.round(taxAmount * 100) / 100,
        totalAmount: Math.round(totalAmount * 100) / 100,
        profitMargin: Math.round(profitMargin * 100) / 100,
      };
    }));
  };

  // Calculate totals
  const calculateTotals = useCallback(() => {
    // Calculate subtotal as sum of (price * quantity - item discount) for each item (before tax)
    const subtotal = cart.reduce((sum, item) => {
      const baseAmount = (item.quantity || 0) * (item.price || 0);
      const itemDiscount = item.discount || 0;
      const itemSubtotal = baseAmount - itemDiscount;
      return sum + itemSubtotal;
    }, 0);

    // Calculate tax amount as sum of individual product taxes
    const taxAmount = cart.reduce((sum, item) => {
      return sum + (item.taxAmount || 0);
    }, 0);

    const discountAmount = discount; // Overall discount amount
    const total = subtotal + taxAmount - discountAmount;

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      discountAmount: Math.round(discountAmount * 100) / 100,
      taxAmount: Math.round(taxAmount * 100) / 100,
      total: Math.round(total * 100) / 100
    };
  }, [cart, discount]);

  // Load suppliers on component mount
  const loadSuppliers = useCallback(async () => {
    try {
      setLoadingSuppliers(true);
      const data = await purchaseOrderService.getAllSuppliers();
      // Ensure data is always an array
      const suppliersArray = Array.isArray(data) ? data : (data?.content || data?.data || []);
      setSuppliers(suppliersArray);

      // Auto-select the first supplier if available and no supplier is selected
      setSupplier(prevSupplier => {
        if (!prevSupplier && suppliersArray.length > 0) {
          console.log('Auto-selected first supplier:', suppliersArray[0].name);
          return suppliersArray[0];
        }
        return prevSupplier;
      });

      console.log('Loaded suppliers:', suppliersArray);
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setSuppliers([]); // Set empty array on error
      enqueueSnackbar('Failed to load suppliers', { variant: 'error' });
    } finally {
      setLoadingSuppliers(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  // Validate supplier form (same as Suppliers.js)
  const validateSupplierForm = () => {
    const newErrors = {};

    // Name is required
    if (!supplierFormValues.name || supplierFormValues.name.trim() === '') {
      newErrors.name = 'Name is required';
    }

    // Phone is required
    if (!supplierFormValues.phone || supplierFormValues.phone.trim() === '') {
      newErrors.phone = 'Phone is required';
    }
    // Phone length limit
    if (supplierFormValues.phone && supplierFormValues.phone.replace(/\D/g, '').length > 15) {
      newErrors.phone = 'Phone must be at most 15 digits';
    }
    // Company-wise duplicate phone check
    const digits = (supplierFormValues.phone || '').replace(/\D/g, '');
    const user = getCurrentUser();
    const companyId = user?.companyId || user?.company?.id || 1;
    const conflict = Array.isArray(suppliers) && suppliers.some(s =>
      (s.companyId === companyId || s.company_id === companyId) &&
      (s.phone || '').replace(/\D/g, '') === digits
    );
    if (conflict) {
      newErrors.phone = 'Phone already exists for this company';
    }
    // Email validation
    if (supplierFormValues.email && supplierFormValues.email.trim() !== '') {
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      if (!pattern.test(supplierFormValues.email)) {
        newErrors.email = 'Invalid email format';
      }
    }

    setSupplierFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle supplier form input change (same as Suppliers.js)
  const handleSupplierFormInputChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === 'phone') {
      v = value.replace(/\D/g, '').slice(0, 15);
      
      // Perform duplicate check in background
      if (v.length >= 7) {
        const digits = v;
        (async () => {
          try {
            const user = getCurrentUser();
            const companyId = user?.companyId || user?.company?.id || 1;
            
            // Local check first
            const conflict = Array.isArray(suppliers) && suppliers.some(s =>
              (s.companyId === companyId || s.company_id === companyId) &&
              (s.phone || '').replace(/\D/g, '') === digits
            );
            
            if (conflict) {
              setSupplierFormErrors(prev => ({ ...prev, phone: 'Phone already exists for this company' }));
            } else {
              const exists = await supplierService.existsPhone(digits);
              if (exists) {
                setSupplierFormErrors(prev => ({ ...prev, phone: 'Phone already exists for this company' }));
              }
            }
          } catch (error) {
            console.error('Error checking duplicate phone:', error);
          }
        })();
      }
    }
    if (name === 'email') {
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      if (v && !pattern.test(v)) {
        setSupplierFormErrors(prev => ({ ...prev, email: 'Invalid email format' }));
      }
    }
    setSupplierFormValues(prev => ({
      ...prev,
      [name]: v
    }));
    // Clear error for this field when user starts typing
    if (supplierFormErrors[name]) {
      setSupplierFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle supplier form submit (same as Suppliers.js)
  const handleSupplierFormSubmit = async () => {
    // Validate form before submission
    if (!validateSupplierForm()) {
      return;
    }

    try {
      const user = getCurrentUser();
      const companyId = user?.companyId || user?.company?.id || 1;
      console.log('Submitting supplier data:', supplierFormValues);
      console.log('Current user:', user);
      console.log('Company ID:', companyId);

      const supplierData = {
        name: supplierFormValues.name.trim(),
        email: supplierFormValues.email.trim(),
        phone: supplierFormValues.phone.trim(),
        contactPerson: supplierFormValues.contact_person.trim(),
        address: supplierFormValues.address.trim(),
        notes: supplierFormValues.notes?.trim() || null,
        website: supplierFormValues.website?.trim() || null,
        companyId: companyId
      };

      console.log('Sending to API:', supplierData);

      const newSupplier = await supplierService.createSupplier(supplierData);

      enqueueSnackbar('Supplier created successfully', { variant: 'success' });

      // Close dialog and reset form
      setAddSupplierDialogOpen(false);
      setSupplierFormValues({
        name: '',
        contact_person: '',
        email: '',
        phone: '',
        website: '',
        address: '',
        notes: '',
        company_id: companyId
      });
      setSupplierFormErrors({});

      // Reload suppliers to include the new one
      await loadSuppliers();

      // Select the new supplier
      setSupplier(newSupplier);
      setSupplierInputValue(''); // Clear input value
    } catch (error) {
      console.error('Error submitting supplier:', error);
      if (error.response?.status === 409) {
        setSupplierFormErrors(prev => ({ ...prev, phone: error.response?.data?.message || 'Phone already exists for this company' }));
      }
      enqueueSnackbar(`Failed to create supplier: ${error.response?.data?.message || error.message}`, { variant: 'error' });
    }
  };

  // Load products for search
  useEffect(() => {
    const loadProducts = async () => {
      try {
        // Fetch all products with a large page size to ensure we get everything
        // Using size=1000 to get all products in one call
        const response = await productService.getAllProducts(0, 1000, 'name', 'asc');
        // Ensure data is always an array
        const productsArray = Array.isArray(response) ? response : (response?.content || response?.data || []);
        setAllProducts(productsArray);
        console.log('[POKeyboard] Loaded products:', productsArray.length, 'products');

        // Debug: Show sample product to verify structure
        if (productsArray.length > 0) {
          console.log('[POKeyboard] Sample product structure:', {
            name: productsArray[0].name,
            sku: productsArray[0].sku,
            barcode: productsArray[0].barcode,
            code: productsArray[0].code,
            hasBarcode: !!productsArray[0].barcode
          });

          // Show how many products have barcodes
          const withBarcodes = productsArray.filter(p => p.barcode && p.barcode.trim().length > 0).length;
          console.log('[POKeyboard] Products with barcodes:', withBarcodes, '/', productsArray.length);
        }
      } catch (error) {
        console.error('[POKeyboard] Error loading products:', error);
        setAllProducts([]);
        enqueueSnackbar('Failed to load products', { variant: 'error' });
      }
    };
    loadProducts();
  }, [enqueueSnackbar]);

  // Load tax rates and set TCS as default
  useEffect(() => {
    const loadTaxRates = async () => {
      try {
        const response = await axiosInstance.get('/taxes');
        const taxRatesArray = Array.isArray(response.data) ? response.data : (response.data?.content || response.data?.data || []);
        setTaxRates(taxRatesArray);

        // Find "No Tax" or "TCS" as default
        const noTax = taxRatesArray.find(tax => tax.name?.toLowerCase() === 'no tax' || tax.rate === 0);
        const tcsTax = taxRatesArray.find(tax => tax.name?.toLowerCase().includes('tcs'));

        const defaultTax = noTax || tcsTax;
        if (defaultTax) {
          setSelectedTax(defaultTax);

          // Update any items that have null tax to use this default tax
          setCart(prevCart => prevCart.map(item =>
            item.tax === null ? { ...item, tax: defaultTax } : item
          ));
        }
        console.log('Loaded tax rates:', taxRatesArray);
      } catch (error) {
        console.error('Error loading tax rates:', error);
        // Set default tax rates if API fails
        const defaultTaxes = [
          { id: 1, name: 'TCS', rate: 1 },
          { id: 2, name: 'GST', rate: 18 },
          { id: 3, name: 'VAT', rate: 12 }
        ];
        setTaxRates(defaultTaxes);

        // Set TCS as default
        setSelectedTax(defaultTaxes[0]);
      }
    };
    loadTaxRates();
  }, []);

  // Auto-focus on first Item Code field when page loads
  useEffect(() => {
    const timer = setTimeout(() => {
      if (itemCodeRefs.current[0]) {
        itemCodeRefs.current[0].focus();
      }
    }, 500); // Small delay to ensure component is fully rendered

    return () => clearTimeout(timer);
  }, []);

  // Handle product input change with dropdown
  const handleProductInputChange = (value, rowIndex) => {
    const newCart = [...cart];
    newCart[rowIndex].itemCode = value;
    setCart(newCart);

    if (value.trim().length > 0) {
      // Filter products based on input (name, SKU, or barcode)
      const searchValue = value.toLowerCase().trim();
      const filtered = allProducts.filter(p =>
        p.name?.toLowerCase().includes(searchValue) ||
        p.sku?.toLowerCase().includes(searchValue) ||
        p.barcode?.toLowerCase().includes(searchValue) ||
        p.code?.toLowerCase().includes(searchValue)
      );

      // Debug logging
      console.log('[POKeyboard] Search value:', value);
      console.log('[POKeyboard] All products count:', allProducts.length);
      console.log('[POKeyboard] Filtered products count:', filtered.length);
      if (allProducts.length > 0) {
        console.log('[POKeyboard] Sample product:', {
          name: allProducts[0].name,
          sku: allProducts[0].sku,
          barcode: allProducts[0].barcode,
          code: allProducts[0].code
        });
      }
      if (filtered.length > 0) {
        console.log('[POKeyboard] First filtered product:', {
          name: filtered[0].name,
          sku: filtered[0].sku,
          barcode: filtered[0].barcode
        });
      }

      setFilteredProducts(filtered);
      setShowProductDropdown(prev => ({ ...prev, [rowIndex]: true }));
      setHighlightIndexByRow(prev => ({ ...prev, [rowIndex]: 0 }));
    } else {
      setShowProductDropdown(prev => ({ ...prev, [rowIndex]: false }));
      setHighlightIndexByRow(prev => ({ ...prev, [rowIndex]: 0 }));
    }
  };

  // Handle product selection from dropdown
  const handleProductSelect = (product, rowIndex) => {
    const purchasePrice = product.purchasePrice || product.sellingPrice || 0;
    const sellingPrice = product.sellingPrice || 0;
    const productId = product.id;

    setCart(prev => {
      // Check if this product already exists in another row
      const existingProductIndex = prev.findIndex(
        (item, idx) => item.product && item.product.id === productId && idx !== rowIndex
      );

      if (existingProductIndex !== -1) {
        // Product already exists - increase quantity instead
        const updatedCart = prev.map((item, index) => {
          if (index === existingProductIndex) {
            const newQuantity = (item.quantity || 1) + 1;
            const baseAmount = newQuantity * (item.price || purchasePrice);
            const discountAmount = item.discount || 0;
            const taxableAmount = baseAmount - discountAmount;
            const taxAmount = item.tax ? taxableAmount * (item.tax.rate / 100) : 0;
            const totalAmount = baseAmount - discountAmount + taxAmount;

            return {
              ...item,
              quantity: newQuantity,
              taxAmount: taxAmount,
              totalAmount: totalAmount
            };
          }
          if (index === rowIndex) {
            // Always clear the scanning row so repeated scans of the same product
            // just increase quantity on the existing row, instead of leaving the
            // barcode text duplicated in this row's item code input.
            return {
              ...item,
              itemCode: '',
              productName: '',
              price: 0,
              quantity: 1,
              discount: 0,
              tax: null,
              taxAmount: 0,
              totalAmount: 0,
              product: null
            };
          }
          return item;
        });

        setShowProductDropdown(prev => ({ ...prev, [rowIndex]: false }));

        // Focus back on the current row's item code field
        setTimeout(() => {
          if (itemCodeRefs.current[rowIndex]) {
            itemCodeRefs.current[rowIndex].focus();
          }
        }, 100);

        return updatedCart;
      } else {
        // New product - add it to the current row
        const updatedCart = prev.map((item, index) => {
          if (index === rowIndex) {
            // Calculate tax amount and total amount using current item quantity
            const baseAmount = (item.quantity || 1) * purchasePrice;
            const discountAmount = item.discount || 0;
            const taxableAmount = baseAmount - discountAmount;
            const taxAmount = product.tax ? taxableAmount * (product.tax.rate / 100) : 0;
            const totalAmount = baseAmount - discountAmount + taxAmount;

            // Calculate profit margin based on purchase after tax per unit
            const purchaseAfterTaxPerUnit = (item.quantity || 1) > 0 ? totalAmount / (item.quantity || 1) : purchasePrice;
            const profitMargin = purchaseAfterTaxPerUnit > 0 ? ((sellingPrice - purchaseAfterTaxPerUnit) / purchaseAfterTaxPerUnit * 100) : 0;

            return {
              ...item,
              itemCode: product.sku || product.name,
              productName: product.name,
              price: purchasePrice,
              sellingPrice: sellingPrice,
              profitMargin: Math.round(profitMargin * 100) / 100,
              tax: product.tax || null,
              taxAmount: taxAmount,
              totalAmount: totalAmount,
              product: product
            };
          }
          return item;
        });

        setShowProductDropdown(prev => ({ ...prev, [rowIndex]: false }));

        // Auto-focus on quantity field after product selection
        setTimeout(() => {
          if (qtyRefs.current[rowIndex]) {
            qtyRefs.current[rowIndex].focus();
          }
        }, 100);

        return updatedCart;
      }
    });
  };

  // Handle product search (for Enter key or barcode scan) - by name, SKU, or barcode
  const handleProductSearch = async (itemCode, rowIndex) => {
    if (!itemCode.trim()) return;

    setSearchingProduct(true);
    try {
      // Search by SKU, barcode, or name (case insensitive) in loaded products
      let product = allProducts.find(p =>
        p.sku?.toLowerCase() === itemCode.trim().toLowerCase() ||
        p.barcode?.toLowerCase() === itemCode.trim().toLowerCase() ||
        p.name?.toLowerCase().includes(itemCode.trim().toLowerCase())
      );

      // If not found in list, try barcode lookup (e.g. scanned barcode via machine)
      if (!product) {
        try {
          const byBarcode = await productService.getProductByBarcode(itemCode.trim());
          if (byBarcode) product = byBarcode;
        } catch (e) {
          // ignore
        }
      }

      if (product) {
        handleProductSelect(product, rowIndex);
      } else {
        // Show dialog to ask if user wants to create new product
        setPendingProductCode(itemCode);
        setPendingRowIndex(rowIndex);
        setProductNotFoundDialogOpen(true);
      }
    } catch (error) {
      enqueueSnackbar('Error searching product', { variant: 'error' });
    } finally {
      setSearchingProduct(false);
    }
  };

  // Handle product not found confirmation
  const handleProductNotFoundConfirm = () => {
    setProductNotFoundDialogOpen(false);
    setProductCreateDialogOpen(true);
  };

  const handleProductNotFoundCancel = () => {
    setProductNotFoundDialogOpen(false);
    setPendingProductCode('');
    setPendingRowIndex(-1);
  };

  // Handle product creation
  const handleProductCreate = async (productData) => {
    try {
      // Create the product
      const newProduct = await productService.createProduct(productData);

      // Reload products to include the new one
      try {
        const response = await productService.getAllProducts(0, 1000, 'name', 'asc');
        const productsArray = Array.isArray(response) ? response : (response?.content || response?.data || []);
        setAllProducts(productsArray);
      } catch (error) {
        console.error('Error reloading products:', error);
      }

      // Close the dialog
      setProductCreateDialogOpen(false);

      // Add the new product to the cart
      if (pendingRowIndex >= 0 && newProduct) {
        handleProductSelect(newProduct, pendingRowIndex);
        enqueueSnackbar(`${newProduct.name} created and added to purchase order`, { variant: 'success' });
      }

      // Reset pending data
      setPendingProductCode('');
      setPendingRowIndex(-1);
    } catch (error) {
      console.error('Failed to create product:', error);
      enqueueSnackbar('Failed to create product', { variant: 'error' });
    }
  };

  const handleProductCreateCancel = () => {
    setProductCreateDialogOpen(false);
    setPendingProductCode('');
    setPendingRowIndex(-1);
  };

  // Auto-focus OK button when dialog opens
  useEffect(() => {
    if (productNotFoundDialogOpen) {
      // Use a longer timeout to ensure dialog is fully rendered
      const timer = setTimeout(() => {
        if (confirmButtonRef.current) {
          confirmButtonRef.current.focus();
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [productNotFoundDialogOpen]);

  // Handle quantity change
  const handleQuantityChange = (rowIndex, newQuantity) => {
    // If field is empty, allow it to be empty (don't force to 1)
    if (newQuantity === '') {
      setCart(prev => prev.map((item, index) => {
        if (index === rowIndex) {
          const baseAmount = 0 * (item.price || 0);
          const discountAmount = item.discount || 0;
          const taxableAmount = baseAmount - discountAmount;
          const taxAmount = item.tax ? taxableAmount * (item.tax.rate / 100) : 0;
          const totalAmount = baseAmount - discountAmount + taxAmount;

          return { ...item, quantity: 0, taxAmount, totalAmount };
        }
        return item;
      }));
      return;
    }

    // If user types 0, set to 0 (they can type more digits)
    if (newQuantity === '0') {
      setCart(prev => prev.map((item, index) => {
        if (index === rowIndex) {
          const baseAmount = 0 * (item.price || 0);
          const discountAmount = item.discount || 0;
          const taxableAmount = baseAmount - discountAmount;
          const taxAmount = item.tax ? taxableAmount * (item.tax.rate / 100) : 0;
          const totalAmount = baseAmount - discountAmount + taxAmount;

          return { ...item, quantity: 0, taxAmount, totalAmount };
        }
        return item;
      }));
      return;
    }

    // Remove leading zeros and parse
    const cleanValue = newQuantity.replace(/^0+/, '');
    const qty = Math.max(0, parseInt(cleanValue) || 0);

    setCart(prev => prev.map((item, index) => {
      if (index === rowIndex) {
        const baseAmount = qty * (item.price || 0);
        const discountAmount = item.discount || 0;
        const taxableAmount = baseAmount - discountAmount;
        const taxAmount = item.tax ? taxableAmount * (item.tax.rate / 100) : 0;
        const totalAmount = baseAmount - discountAmount + taxAmount;

        return { ...item, quantity: qty, taxAmount, totalAmount };
      }
      return item;
    }));
  };

  // Handle price change
  const handlePriceChange = (rowIndex, newPrice) => {
    // If field is empty, set to 0
    if (newPrice === '') {
      setCart(prev => prev.map((item, index) =>
        index === rowIndex ? { ...item, price: 0, taxAmount: 0, totalAmount: 0, profitMargin: 0 } : item
      ));
      return;
    }

    // Remove leading zeros and parse
    const cleanValue = newPrice.replace(/^0+/, '');
    const price = Math.max(0, parseFloat(cleanValue) || 0);

    setCart(prev => prev.map((item, index) => {
      if (index !== rowIndex) return item;

      const quantity = item.quantity || 0;
      const baseAmount = quantity * price;
      const discountAmount = item.discount || 0;
      const taxableAmount = baseAmount - discountAmount;
      const taxRate = item.tax?.rate || 0;
      const taxAmount = taxRate > 0 ? taxableAmount * (taxRate / 100) : 0;
      const totalAmount = baseAmount - discountAmount + taxAmount;

      // Recalculate profit margin based on purchase after tax per unit
      const purchaseAfterTaxPerUnit = quantity > 0 ? totalAmount / quantity : price;
      const sellingPrice = item.sellingPrice || 0;
      const profitMargin = purchaseAfterTaxPerUnit > 0 ? ((sellingPrice - purchaseAfterTaxPerUnit) / purchaseAfterTaxPerUnit * 100) : 0;

      return {
        ...item,
        price,
        taxAmount,
        totalAmount,
        profitMargin: Math.round(profitMargin * 100) / 100,
      };
    }));
  };

  // Handle selling price change
  const handleSellingPriceChange = (rowIndex, newSellingPrice) => {
    // If field is empty, set to 0
    if (newSellingPrice === '') {
      setCart(prev => prev.map((item, index) =>
        index === rowIndex ? { ...item, sellingPrice: 0, profitMargin: 0 } : item
      ));
      return;
    }

    // Remove leading zeros and parse
    const cleanValue = newSellingPrice.replace(/^0+/, '');
    const sellingPrice = Math.max(0, parseFloat(cleanValue) || 0);
    const item = cart[rowIndex];
    const quantity = item.quantity || 0;
    const baseAmount = quantity * (item.price || 0);
    const discountAmount = item.discount || 0;
    const taxableAmount = baseAmount - discountAmount;
    const taxRate = item.tax?.rate || 0;
    const taxAmount = taxRate > 0 ? taxableAmount * (taxRate / 100) : 0;
    const totalAmount = baseAmount - discountAmount + taxAmount;
    const purchaseAfterTaxPerUnit = quantity > 0 ? totalAmount / quantity : (item.price || 0);
    const profitMargin = purchaseAfterTaxPerUnit > 0 ? ((sellingPrice - purchaseAfterTaxPerUnit) / purchaseAfterTaxPerUnit * 100) : 0;

    setCart(prev => prev.map((item, index) =>
      index === rowIndex ? { ...item, sellingPrice, profitMargin: Math.round(profitMargin * 100) / 100 } : item
    ));
  };

  // Handle profit margin change
  const handleProfitMarginChange = (rowIndex, newMargin) => {
    // If field is empty, set to 0
    if (newMargin === '') {
      setCart(prev => prev.map((item, index) =>
        index === rowIndex ? { ...item, profitMargin: 0, sellingPrice: 0 } : item
      ));
      return;
    }

    // Remove leading zeros and parse
    const cleanValue = newMargin.replace(/^0+/, '');
    const margin = parseFloat(cleanValue) || 0;

    setCart(prev => prev.map((item, index) => {
      if (index !== rowIndex) return item;

      // Calculate purchase after tax per unit
      const quantity = item.quantity || 0;
      const baseAmount = quantity * (item.price || 0);
      const discountAmount = item.discount || 0;
      const taxableAmount = baseAmount - discountAmount;
      const taxRate = item.tax?.rate || 0;
      const taxAmount = taxRate > 0 ? taxableAmount * (taxRate / 100) : 0;
      const totalAmount = baseAmount - discountAmount + taxAmount;
      const purchaseAfterTaxPerUnit = quantity > 0 ? totalAmount / quantity : (item.price || 0);

      // Calculate selling price based on purchase after tax per unit
      const sellingPrice = purchaseAfterTaxPerUnit + (purchaseAfterTaxPerUnit * margin / 100);

      return { ...item, profitMargin: margin, sellingPrice: Math.round(sellingPrice * 100) / 100 };
    }));
  };

  // Handle discount change with bidirectional conversion
  const handleDiscountChange = (rowIndex, value, type) => {
    setCart(prev => prev.map((item, index) => {
      if (index === rowIndex) {
        const baseAmount = (item.quantity || 0) * (item.price || 0);
        let discountPercent = 0;
        let discountAmt = 0;

        // If field is empty, set to 0
        if (value === '' || value === null || value === undefined) {
          discountPercent = 0;
          discountAmt = 0;
        } else {
          const numValue = parseFloat(value) || 0;

          if (type === 'percentage') {
            // User entered percentage - calculate amount
            discountPercent = Math.min(numValue, 100); // Cap at 100%
            discountAmt = baseAmount * (discountPercent / 100);
          } else {
            // User entered amount - calculate percentage
            discountAmt = Math.min(numValue, baseAmount); // Cap at base amount
            discountPercent = baseAmount > 0 ? (discountAmt / baseAmount) * 100 : 0;
          }
        }

        const afterDiscount = baseAmount - discountAmt;
        const taxAmount = item.tax ? afterDiscount * (item.tax.rate / 100) : 0;
        const totalAmount = afterDiscount + taxAmount;

        // Calculate profit margin based on purchase after tax per unit
        const purchaseAfterTaxPerUnit = (item.quantity || 0) > 0 ? totalAmount / (item.quantity || 0) : (item.price || 0);
        const profitMargin = purchaseAfterTaxPerUnit > 0 ? ((item.sellingPrice - purchaseAfterTaxPerUnit) / purchaseAfterTaxPerUnit * 100) : 0;

        return {
          ...item,
          discountPercentage: Math.round(discountPercent * 100) / 100,
          discountAmount: Math.round(discountAmt * 100) / 100,
          discount: discountAmt, // Keep for backward compatibility
          taxAmount: Math.round(taxAmount * 100) / 100,
          totalAmount: Math.round(totalAmount * 100) / 100,
          profitMargin: Math.round(profitMargin * 100) / 100
        };
      }
      return item;
    }));
  };

  // Handle overall discount change
  const handleOverallDiscountChange = (newDiscount) => {
    // If field is empty, set to 0
    if (newDiscount === '') {
      setDiscount(0);
      return;
    }

    // Remove leading zeros and parse
    const cleanValue = newDiscount.replace(/^0+/, '');
    const discountValue = parseFloat(cleanValue) || 0;
    setDiscount(discountValue);
  };

  // Handle tax change
  const handleTaxChange = (rowIndex, taxId) => {
    const selectedProductTax = taxRates.find(t => t.id === taxId);
    setCart(prev => prev.map((item, index) => {
      if (index === rowIndex) {
        // Calculate tax amount for this item (after discount)
        const baseAmount = (item.quantity || 0) * (item.price || 0);
        const discountAmount = item.discount || 0;
        const taxableAmount = baseAmount - discountAmount;
        const taxAmount = selectedProductTax ? taxableAmount * (selectedProductTax.rate / 100) : 0;
        const totalAmount = baseAmount - discountAmount + taxAmount;

        // Recalculate profit margin based on purchase after tax per unit
        const purchaseAfterTaxPerUnit = (item.quantity || 0) > 0 ? totalAmount / (item.quantity || 0) : (item.price || 0);
        const profitMargin = purchaseAfterTaxPerUnit > 0 ? ((item.sellingPrice - purchaseAfterTaxPerUnit) / purchaseAfterTaxPerUnit * 100) : 0;

        return {
          ...item,
          tax: selectedProductTax || null,
          taxAmount: taxAmount,
          totalAmount: totalAmount,
          profitMargin: Math.round(profitMargin * 100) / 100
        };
      }
      return item;
    }));
  };

  // Add new row
  const addNewRow = () => {
    const newId = Math.max(...cart.map(item => item.id)) + 1;
    setCart(prev => [...prev, {
      id: newId,
      itemCode: '',
      productName: '',
      quantity: 1,
      price: 0,
      sellingPrice: 0,
      profitMargin: 0,
      tax: selectedTax,
      taxAmount: 0,
      totalAmount: 0,
      discount: 0,
      discountPercentage: 0,
      discountAmount: 0,
      product: null
    }]);
    setSelectedRowIndex(cart.length);

    // Auto-focus on the new row's item code field
    setTimeout(() => {
      const newRowIndex = cart.length; // The new row will be at this index
      if (itemCodeRefs.current[newRowIndex]) {
        itemCodeRefs.current[newRowIndex].focus();
      }
    }, 100);
  };

  // Remove row
  const removeRow = (rowIndex) => {
    if (cart.length === 1) {
      // Reset the only row
      setCart([{ id: 1, itemCode: '', productName: '', quantity: 1, price: 0, sellingPrice: 0, profitMargin: 0, tax: selectedTax, taxAmount: 0, totalAmount: 0, discount: 0, discountPercentage: 0, discountAmount: 0, product: null }]);
      setSelectedRowIndex(0);
    } else {
      setCart(prev => prev.filter((_, index) => index !== rowIndex));
      setSelectedRowIndex(null);
    }
    setDeleteConfirmOpen(false);
    setRowToDelete(null);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, rowIndex, field) => {
    if (e.key === 'ArrowDown' && showProductDropdown[rowIndex]) {
      e.preventDefault();
      const next = Math.min((highlightIndexByRow[rowIndex] ?? 0) + 1, filteredProducts.length - 1);
      setHighlightIndexByRow(prev => ({ ...prev, [rowIndex]: next }));

      // Scroll the highlighted item into view
      setTimeout(() => {
        const dropdownElement = document.querySelector(`[data-dropdown-row="${rowIndex}"]`);
        if (dropdownElement) {
          const highlightedItem = dropdownElement.querySelector(`[data-item-index="${next}"]`);
          if (highlightedItem) {
            highlightedItem.scrollIntoView({ block: 'nearest' });
          }
        }
      }, 0);
    } else if (e.key === 'ArrowUp' && showProductDropdown[rowIndex]) {
      e.preventDefault();
      const prevIdx = Math.max((highlightIndexByRow[rowIndex] ?? 0) - 1, 0);
      setHighlightIndexByRow(prev => ({ ...prev, [rowIndex]: prevIdx }));

      // Scroll the highlighted item into view
      setTimeout(() => {
        const dropdownElement = document.querySelector(`[data-dropdown-row="${rowIndex}"]`);
        if (dropdownElement) {
          const highlightedItem = dropdownElement.querySelector(`[data-item-index="${prevIdx}"]`);
          if (highlightedItem) {
            highlightedItem.scrollIntoView({ block: 'nearest' });
          }
        }
      }, 0);
    } else if (e.key === 'Escape' && showProductDropdown[rowIndex]) {
      e.preventDefault();
      setShowProductDropdown(prev => ({ ...prev, [rowIndex]: false }));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (field === 'itemCode' && cart[rowIndex].itemCode.trim()) {
        // If dropdown is visible, select highlighted item instead of auto-selecting by partial text
        if (showProductDropdown[rowIndex] && filteredProducts.length > 0) {
          const hi = highlightIndexByRow[rowIndex] ?? 0;
          const product = filteredProducts[hi];
          if (product) {
            handleProductSelect(product, rowIndex);
            return;
          }
        }
        // Only auto-select on Enter if exact SKU or barcode matches OR only one filtered match
        const value = cart[rowIndex].itemCode.trim();
        const exact = allProducts.find(p =>
          p.sku?.toLowerCase() === value.toLowerCase() ||
          p.barcode?.toLowerCase() === value.toLowerCase()
        );
        if (exact) {
          handleProductSelect(exact, rowIndex);
        } else if (filteredProducts.length === 1) {
          handleProductSelect(filteredProducts[0], rowIndex);
        } else {
          // No product found - search and show dialog if not found
          setShowProductDropdown(prev => ({ ...prev, [rowIndex]: false }));
          handleProductSearch(value, rowIndex);
        }
      } else if (field === 'quantity') {
        // Move to purchase price field
        if (priceRefs.current[rowIndex]) {
          priceRefs.current[rowIndex].focus();
        }
      } else if (field === 'price') {
        // Move to profit margin field
        if (profitMarginRefs.current[rowIndex]) {
          profitMarginRefs.current[rowIndex].focus();
        }
      } else if (field === 'profitMargin') {
        // Move to selling price field
        if (sellingPriceRefs.current[rowIndex]) {
          sellingPriceRefs.current[rowIndex].focus();
        }
      } else if (field === 'sellingPrice' && rowIndex === cart.length - 1) {
        addNewRow();
      }
    } else if (e.key === 'Tab') {
      // Handle Tab navigation between fields
      e.preventDefault();
      if (field === 'itemCode') {
        if (qtyRefs.current[rowIndex]) {
          qtyRefs.current[rowIndex].focus();
        }
      } else if (field === 'quantity') {
        if (priceRefs.current[rowIndex]) {
          priceRefs.current[rowIndex].focus();
        }
      } else if (field === 'price') {
        if (profitMarginRefs.current[rowIndex]) {
          profitMarginRefs.current[rowIndex].focus();
        }
      } else if (field === 'profitMargin') {
        if (sellingPriceRefs.current[rowIndex]) {
          sellingPriceRefs.current[rowIndex].focus();
        }
      } else if (field === 'sellingPrice') {
        if (rowIndex === cart.length - 1) {
          addNewRow();
        } else if (itemCodeRefs.current[rowIndex + 1]) {
          itemCodeRefs.current[rowIndex + 1].focus();
        }
      }
    }
  };

  // Handle save purchase order
  const handleSavePurchaseOrder = async () => {
    if (!supplier) {
      enqueueSnackbar('Please select a supplier', { variant: 'error' });
      return;
    }

    const validItems = cart.filter(item => item.product && item.quantity > 0);
    if (validItems.length === 0) {
      enqueueSnackbar('Please add at least one product', { variant: 'error' });
      return;
    }

    try {
      const totals = calculateTotals();

      // Update selling prices and taxes for products if changed
      for (const item of validItems) {
        const needsPriceUpdate = item.sellingPrice && item.sellingPrice !== item.product.sellingPrice;
        const needsTaxUpdate = item.tax && item.tax.id !== item.product.tax?.id;

        if (needsPriceUpdate || needsTaxUpdate) {
          try {
            // Fetch current product data to ensure we have all required fields
            const currentProduct = await productService.getProductById(item.product.id);

            // Prepare update request with all required fields
            const updateData = {
              name: currentProduct.name,
              sku: currentProduct.sku,
              barcode: currentProduct.barcode,
              description: currentProduct.description,
              categoryId: currentProduct.category?.id,
              unitId: currentProduct.unit?.id,
              purchasePrice: currentProduct.purchasePrice || 0,
              sellingPrice: needsPriceUpdate ? item.sellingPrice : currentProduct.sellingPrice,
              minSalePrice: currentProduct.minSalePrice || 0,
              mrp: currentProduct.mrp || 0,
              hsnCode: currentProduct.hsnCode,
              taxId: needsTaxUpdate ? item.tax.id : (currentProduct.tax?.id || null),
              quantity: currentProduct.quantity || 0,
              reorderLevel: currentProduct.reorderLevel || 0,
              imageUrl: currentProduct.imageUrl,
              companyId: currentProduct.company?.id,
              status: currentProduct.status || 'ACTIVE'
            };

            await productService.updateProduct(item.product.id, updateData);
            console.log(`Updated product ${item.product.id}: ${needsPriceUpdate ? 'selling price=' + item.sellingPrice : ''} ${needsTaxUpdate ? 'tax=' + item.tax.name : ''}`);
          } catch (error) {
            console.error(`Error updating product ${item.product.id}:`, error);
            enqueueSnackbar(`Warning: Could not update product ${item.product.name}`, { variant: 'warning' });
          }
        }
      }

      const purchaseOrder = {
        supplierId: supplier.id,
        orderDate: purchaseDate,
        companyId: JSON.parse(localStorage.getItem('user'))?.companyId || 1,
        invoiceNumber: invoiceNumber || null,
        invoiceDate: invoiceDate || null,
        items: validItems.map(item => ({
          productId: item.product.id,
          quantity: item.quantity,
          unitPrice: item.price,
          discountPercentage: item.discountPercentage || 0,  // Send discount percentage
          discountAmount: item.discountAmount || 0,  // Send discount amount
          discount: item.discountAmount || 0,  // Legacy field for backward compatibility
          taxId: item.tax?.id || null  // Send individual item tax ID
        })),
        paymentMethod: paymentMethod,
        status: "RECEIVED_COMPLETE",
        notes: notes || '',
        subtotal: totals.subtotal,
        taxAmount: totals.taxAmount,
        total: totals.total,
        taxId: null, // No overall tax - using individual product taxes
        discountPercentage: 0,  // Overall discount percentage (not used in current UI)
        discountAmount: discount || 0,  // Overall discount amount
        discount: discount || 0  // Legacy field for backward compatibility
      };

      console.log('Creating purchase order:', purchaseOrder);
      const result = await purchaseOrderService.createPurchaseOrder(purchaseOrder);

      enqueueSnackbar(`Purchase order #${result.id || 'New'} created successfully!`, { variant: 'success' });

      // After successful save, go back to the View Purchases page
      navigate('/purchases/view-orders');

    } catch (error) {
      console.error('Error creating purchase order:', error);
      enqueueSnackbar('Error creating purchase order: ' + (error.message || 'Unknown error'), { variant: 'error' });
    }
  };

  const totals = calculateTotals();
  const totalItems = cart.filter(item => item.product && item.quantity > 0).length;
  const totalQuantity = cart.reduce((sum, item) => sum + (item.quantity || 0), 0);

  // Focus Delete Yes button when dialog opens
  useEffect(() => {
    if (deleteConfirmOpen) {
      setTimeout(() => {
        deleteYesRef.current?.focus();
      }, 100);
    }
  }, [deleteConfirmOpen]);

  // Keyboard shortcut Ctrl+S and Delete
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Ctrl+S: Save
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSavePurchaseOrder();
      }

      // Delete key: Remove row
      if (e.key === 'Delete') {
        const activeElement = document.activeElement;
        const isInput = activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.isContentEditable;

        if (!isInput && selectedRowIndex !== null && cart[selectedRowIndex]) {
          setRowToDelete(selectedRowIndex);
          setDeleteConfirmOpen(true);
        }
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleSavePurchaseOrder, selectedRowIndex, cart]);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f1f1f1', fontFamily: '"Roboto", sans-serif', overflow: 'hidden' }}>
      {/* 1. Header Section */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1,
          backgroundColor: '#154c79', // Deep Blue to match Sales
          color: 'white',
          borderRadius: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 'bold', letterSpacing: 1 }}>
          PURCHASE-ORDER
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Purchase Date: <span style={{ fontWeight: 'normal' }}>{new Date(purchaseDate).toLocaleDateString('en-GB')}</span>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate(-1)}
              sx={{ backgroundColor: 'white', color: '#154c79', textTransform: 'none', height: 28, '&:hover': { backgroundColor: '#f5f5f5' } }}
              startIcon={<span style={{ fontSize: '1.2em', lineHeight: 0 }}>←</span>}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setCart([{ id: 1, itemCode: '', productName: '', quantity: 1, price: 0, sellingPrice: 0, profitMargin: 0, tax: selectedTax, taxAmount: 0, totalAmount: 0, discount: 0, discountPercentage: 0, discountAmount: 0, product: null }]);
                setInvoiceNumber('');
                setInvoiceDate('');
                setNotes('');
                setDiscount(0);
                if (suppliers.length > 0) setSupplier(suppliers[0]);
                else setSupplier(null);
              }}
              sx={{ borderColor: '#ef5350', color: '#ef5350', backgroundColor: 'white', textTransform: 'none', height: 28, '&:hover': { backgroundColor: '#ffebee', borderColor: '#ef5350' } }}
              startIcon={<span style={{ fontSize: '1.2em', lineHeight: 0 }}>⟲</span>}
            >
              Clear
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* 2. Top Info Section - Restructured to match POS */}
      <Paper elevation={1} sx={{ p: 1.5, m: 1, borderRadius: 1 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Left Side: Inputs */}
          <Grid item xs={12} md={9}>
            <Grid container spacing={2} alignItems="flex-end">
              {/* Supplier Search */}
              <Grid item xs={12} md={3}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555', display: 'block', mb: 0.5 }}>Supplier</Typography>
                <Autocomplete
                  options={suppliers}
                  getOptionLabel={(option) => {
                    if (option && option.isAddNew) return 'Add New Supplier';
                    return option?.name || '';
                  }}
                  value={supplier}
                  inputValue={supplierInputValue}
                  onInputChange={(event, newInputValue) => {
                    setSupplierInputValue(newInputValue);
                  }}
                  onChange={(event, newValue) => {
                    if (newValue && newValue.isAddNew) {
                      const user = getCurrentUser();
                      const companyId = user?.companyId || user?.company?.id || 1;
                      setSupplierFormValues({
                        name: supplierInputValue.trim() || '',
                        contact_person: '',
                        email: '',
                        phone: '',
                        website: '',
                        address: '',
                        notes: '',
                        company_id: companyId
                      });
                      setSupplierFormErrors({});
                      setAddSupplierDialogOpen(true);
                      setSupplierInputValue('');
                    } else {
                      setSupplier(newValue);
                      setSupplierInputValue('');
                    }
                  }}
                  filterOptions={(options, params) => {
                    const filtered = options.filter((option) => {
                      if (option.isAddNew) return false;
                      const searchText = params.inputValue.toLowerCase();
                      return (
                        option.name?.toLowerCase().includes(searchText) ||
                        option.phone?.toLowerCase().includes(searchText) ||
                        option.email?.toLowerCase().includes(searchText)
                      );
                    });
                    filtered.unshift({ isAddNew: true, id: 'add-new-supplier' });
                    return filtered;
                  }}
                  renderInput={(params) => <TextField {...params} placeholder="Search supplier..." sx={{ '& .MuiInputBase-root': { fontSize: '15px', backgroundColor: 'white' } }} />}
                  renderOption={(props, option) => {
                    if (option.isAddNew) {
                      return (
                        <Box component="li" {...props} key="add-new-supplier" sx={{ borderBottom: '1px solid #e0e0e0', backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#e3f2fd' } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', py: 0.5 }}>
                            <PersonAddIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main' }}>Add New Supplier</Typography>
                          </Box>
                        </Box>
                      );
                    }
                    return (
                      <Box component="li" {...props} key={option.id}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{option.name}</Typography>
                          {option.phone && <Typography variant="caption" color="text.secondary">{option.phone}</Typography>}
                        </Box>
                      </Box>
                    );
                  }}
                  fullWidth
                  size="small"
                />
              </Grid>

              {/* Purchase Details Group */}
              <Grid item xs={12} md={7}>
                <Grid container spacing={1}>
                  <Grid item xs={3}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555', display: 'block', mb: 0.5 }}>Date</Typography>
                    <TextField
                      type="date"
                      value={purchaseDate}
                      onChange={(e) => setPurchaseDate(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ '& .MuiInputBase-root': { fontSize: '14px', backgroundColor: 'white' } }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555', display: 'block', mb: 0.5 }}>Inv No</Typography>
                    <TextField
                      placeholder="Inv No"
                      value={invoiceNumber}
                      onChange={(e) => setInvoiceNumber(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ '& .MuiInputBase-root': { fontSize: '14px', backgroundColor: 'white' } }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555', display: 'block', mb: 0.5 }}>Invoice Date</Typography>
                    <TextField
                      type="date"
                      value={invoiceDate}
                      onChange={(e) => setInvoiceDate(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ '& .MuiInputBase-root': { fontSize: '14px', backgroundColor: 'white' } }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555', display: 'block', mb: 0.5 }}>Pay Mode</Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      sx={{ backgroundColor: 'white', fontSize: '14px', height: '40px' }}
                    >
                      <MenuItem value="CASH">💵 Cash</MenuItem>
                      <MenuItem value="CARD">💳 Card</MenuItem>
                      <MenuItem value="UPI">📱 UPI</MenuItem>
                      <MenuItem value="CREDIT">📝 Credit</MenuItem>
                    </Select>
                  </Grid>
                </Grid>
              </Grid>

              {/* Items / Qty Stats */}
              <Grid item xs={12} md={2} sx={{ display: 'flex', gap: 1, pb: 0.2 }}>
                <Chip
                  label={`Items: ${totalItems}`}
                  sx={{ backgroundColor: '#e0f2f1', color: '#00695c', fontWeight: 'bold', border: '1px solid #b2dfdb', height: '40px', fontSize: '16px', flex: 1 }}
                />
                <Chip
                  label={`Qty: ${totalQuantity}`}
                  sx={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', border: '1px solid #bbdefb', height: '40px', fontSize: '16px', flex: 1 }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Right Side: Big Total */}
          <Grid item xs={12} md={3} sx={{ textAlign: 'right' }}>
            <Typography variant="h2" sx={{ fontWeight: 'bold', color: '#e74c3c', lineHeight: 1, fontSize: '3.5rem' }}>
              {totals.total.toFixed(2)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Items Table - Scrollable */}
      <Box sx={{ flexGrow: 1, px: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)' }}>
        <TableContainer component={Paper} elevation={1} sx={{ flexGrow: 1, borderRadius: 1 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableHeadCell sx={{ backgroundColor: '#154c79', color: 'white', width: '50px' }}>S.No</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ backgroundColor: '#154c79', color: 'white', width: '150px' }}>Item Code</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ backgroundColor: '#154c79', color: 'white', width: '200px' }}>Product Name</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ backgroundColor: '#154c79', color: 'white', width: '70px' }}>Qty</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ backgroundColor: '#154c79', color: 'white', width: '110px' }}>Purchase Price</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ backgroundColor: '#154c79', color: 'white', width: '170px' }}>Discount (% / ₹)</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ backgroundColor: '#154c79', color: 'white', width: '130px' }}>Tax</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ backgroundColor: '#154c79', color: 'white', width: '120px', whiteSpace: 'nowrap' }}>Purchase After Tax</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ backgroundColor: '#154c79', color: 'white', width: '100px' }}>Profit %</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ backgroundColor: '#154c79', color: 'white', width: '110px' }}>Selling Price</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ backgroundColor: '#154c79', color: 'white', width: '100px' }}>Amount</StyledTableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cart.map((item, index) => {
                const isSearching = !item.product;
                const isSelected = selectedRowIndex === index;
                const rowBg = isSelected ? '#e3f2fd' : (isSearching ? '#fffde7' : (index % 2 === 0 ? '#f8f9fb' : 'white'));
                return (
                  <TableRow
                    key={item.id}
                    sx={{ backgroundColor: rowBg, cursor: 'pointer' }}
                    onFocus={() => setSelectedRowIndex(index)}
                    onClick={() => setSelectedRowIndex(index)}
                  >
                    <StyledTableCell>{index + 1}</StyledTableCell>
                    <StyledTableCell sx={{ position: 'relative', overflow: 'visible', zIndex: 1 }}>
                      <TextField
                        size="small"
                        value={item.itemCode}
                        onChange={(e) => handleProductInputChange(e.target.value, index)}
                        onKeyDown={(e) => {
                          // CHECK: If we have options in the list, assume the user is trying to select one.
                          // Do NOT fire the manual search in this case, to avoid race conditions.
                          const hasOptions = showProductDropdown[index] && filteredProducts.length > 0;
                          if (e.key === 'Enter') {
                            if (hasOptions) {
                              // Let normal handleKeyDown or the dropdown click handle it
                              handleKeyDown(e, index, 'itemCode');
                              return;
                            }
                            // Proceed to manual search
                          }
                          handleKeyDown(e, index, 'itemCode');
                        }}
                        placeholder="Type to search..."
                        fullWidth
                        inputRef={(el) => {
                          if (itemCodeRefs.current) {
                            itemCodeRefs.current[index] = el;
                          }
                        }}
                        sx={{ '& input': { fontSize: '15px' } }}
                      />
                      {showProductDropdown[index] && filteredProducts.length > 0 && (
                        <Paper
                          data-dropdown-row={index}
                          sx={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 9999,
                            maxHeight: 200,
                            overflow: 'auto',
                            border: '1px solid #ccc',
                            borderRadius: 1,
                            backgroundColor: 'white',
                            boxShadow: 2,
                            minWidth: 300
                          }}
                        >
                          {filteredProducts.slice(0, 10).map((product, optIdx) => (
                            <Box
                              key={product.id}
                              data-item-index={optIdx}
                              sx={{
                                p: 1,
                                cursor: 'pointer',
                                backgroundColor: (highlightIndexByRow[index] ?? 0) === optIdx ? '#eaf2fe' : 'transparent',
                                '&:hover': { backgroundColor: '#f5f5f5' },
                                borderBottom: '1px solid #eee'
                              }}
                              onClick={() => handleProductSelect(product, index)}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {product.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                SKU: {product.sku} | Stock: {product.quantity || 0} | Price: ₹{product.purchasePrice || product.sellingPrice || 0}
                              </Typography>
                            </Box>
                          ))}
                        </Paper>
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2">
                        {item.productName || '-'}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={item.quantity === 0 ? '' : item.quantity}
                        onChange={(e) => handleQuantityChange(index, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => handleKeyDown(e, index, 'quantity')}
                        inputProps={{ min: 1 }}
                        sx={{
                          width: 80,
                          '& input': {
                            '-moz-appearance': 'textfield',
                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                              '-webkit-appearance': 'none',
                              margin: 0,
                            }
                          }
                        }}
                        inputRef={(el) => {
                          if (qtyRefs.current) {
                            qtyRefs.current[index] = el;
                          }
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={item.price === 0 ? '' : item.price}
                        onChange={(e) => handlePriceChange(index, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => handleKeyDown(e, index, 'price')}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{
                          width: 100,
                          '& input': {
                            '-moz-appearance': 'textfield',
                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                              '-webkit-appearance': 'none',
                              margin: 0,
                            }
                          }
                        }}
                        inputRef={(el) => {
                          if (priceRefs.current) {
                            priceRefs.current[index] = el;
                          }
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Tooltip title="Discount Percentage">
                          <TextField
                            size="small"
                            type="number"
                            value={item.discountPercentage || ''}
                            onChange={(e) => handleDiscountChange(index, e.target.value, 'percentage')}
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) => handleKeyDown(e, index, 'discountPercent')}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                            sx={{
                              width: 75,
                              '& input': {
                                '-moz-appearance': 'textfield',
                                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                  '-webkit-appearance': 'none',
                                  margin: 0,
                                }
                              }
                            }}
                            placeholder="0"
                          />
                        </Tooltip>
                        <Tooltip title="Discount Amount">
                          <TextField
                            size="small"
                            type="number"
                            value={item.discountAmount || ''}
                            onChange={(e) => handleDiscountChange(index, e.target.value, 'amount')}
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) => handleKeyDown(e, index, 'discountAmount')}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{
                              width: 85,
                              '& input': {
                                '-moz-appearance': 'textfield',
                                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                  '-webkit-appearance': 'none',
                                  margin: 0,
                                }
                              }
                            }}
                            placeholder="0"
                          />
                        </Tooltip>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={item.tax?.id || ''}
                          onChange={(e) => handleTaxChange(index, e.target.value)}
                          displayEmpty
                        >
                          {taxRates
                            .filter(tax => (tax.status === 'ACTIVE' || !tax.status) || (item.tax && tax.id === item.tax.id))
                            .map((tax) => (
                              <MenuItem key={tax.id} value={tax.id}>
                                {tax.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </StyledTableCell>
                    <StyledTableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={getPurchaseAfterTaxPerUnit(item) === 0 ? '' : getPurchaseAfterTaxPerUnit(item)}
                        onChange={(e) => handlePurchaseAfterTaxChange(index, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => handleKeyDown(e, index, 'purchaseAfterTax')}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{
                          width: 110,
                          '& input': {
                            '-moz-appearance': 'textfield',
                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                              '-webkit-appearance': 'none',
                              margin: 0,
                            }
                          }
                        }}
                        placeholder="0"
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={item.profitMargin === 0 ? '' : item.profitMargin}
                        onChange={(e) => handleProfitMarginChange(index, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => handleKeyDown(e, index, 'profitMargin')}
                        inputProps={{ step: 0.01 }}
                        sx={{
                          width: 90,
                          '& input': {
                            '-moz-appearance': 'textfield',
                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                              '-webkit-appearance': 'none',
                              margin: 0,
                            }
                          }
                        }}
                        InputProps={{
                          endAdornment: <Typography variant="caption" sx={{ ml: 0.5 }}>%</Typography>
                        }}
                        inputRef={(el) => {
                          if (profitMarginRefs.current) {
                            profitMarginRefs.current[index] = el;
                          }
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={item.sellingPrice === 0 ? '' : item.sellingPrice}
                        onChange={(e) => handleSellingPriceChange(index, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => handleKeyDown(e, index, 'sellingPrice')}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{
                          width: 100,
                          '& input': {
                            '-moz-appearance': 'textfield',
                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                              '-webkit-appearance': 'none',
                              margin: 0,
                            }
                          }
                        }}
                        inputRef={(el) => {
                          if (sellingPriceRefs.current) {
                            sellingPriceRefs.current[index] = el;
                          }
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(item.totalAmount || (item.quantity * item.price))}
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>

      </Box>

      {/* 4. Footer Section - Exactly matching Sales POS */}
      <Paper elevation={2} sx={{ p: 1, m: 1, mt: 0, borderRadius: 1 }}>
        <Grid container spacing={2} justifyContent="space-between" alignItems="center">

          {/* Left: Overall Discount */}
          <Grid item xs={12} md={3} sx={{ pl: 2 }}>
            <TextField
              label="Overall Discount"
              type="number"
              variant="outlined"
              size="small"
              fullWidth
              value={discount === 0 ? '' : discount}
              onChange={(e) => handleOverallDiscountChange(e.target.value)}
              onFocus={(e) => e.target.select()}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                style: { fontSize: '15px' }
              }}
              InputLabelProps={{ style: { fontSize: '15px' } }}
              sx={{
                backgroundColor: 'white',
                '& input': {
                  '-moz-appearance': 'textfield',
                  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                    '-webkit-appearance': 'none',
                    margin: 0,
                  }
                }
              }}
            />
          </Grid>

          {/* Right: Net Amount & Breakdown */}
          <Grid item xs={12} md={5}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '16px' }}>Subtotal:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '16px' }}>{totals.subtotal.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '16px' }}>Tax:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '16px' }}>{totals.taxAmount.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '16px' }}>Discount:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '16px' }}>{totals.discountAmount.toFixed(2)}</Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ backgroundColor: '#e3f2fd', p: 1.5, borderRadius: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', mb: 1, height: '100%' }}>
                  <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 'bold' }}>Net Amount</Typography>
                  <Typography variant="h5" sx={{ color: '#1565c0', fontWeight: 'bold' }}>{totals.total.toFixed(2)}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSavePurchaseOrder}
                  sx={{
                    backgroundColor: '#27ae60 !important',
                    backgroundImage: 'none !important',
                    color: 'white !important',
                    opacity: '1 !important',
                    fontWeight: 'bold',
                    fontSize: '18px',
                    textTransform: 'none',
                    py: 1.5,
                    '&:hover': { backgroundColor: '#219150 !important' },
                    '&.Mui-disabled': { backgroundColor: '#27ae60 !important', color: 'white !important', opacity: 0.7 }
                  }}
                  startIcon={<SaveIcon sx={{ fontSize: '24px', marginRight: '8px' }} />}
                >
                  Save Bill
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Product Not Found Dialog */}
      <Dialog
        open={productNotFoundDialogOpen}
        onClose={handleProductNotFoundCancel}
        maxWidth="sm"
        fullWidth
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.altKey) {
            e.preventDefault();
            handleProductNotFoundConfirm();
          }
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#f39c12', color: 'white', fontWeight: 'bold' }}>
          Product Not Found
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1">
              No product found with the name or SKU: <strong>{pendingProductCode}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Would you like to create a new product?
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleProductNotFoundCancel}>
            Cancel
          </Button>
          <Button
            ref={confirmButtonRef}
            variant="contained"
            color="primary"
            onClick={handleProductNotFoundConfirm}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleProductNotFoundConfirm();
              }
            }}
          >
            Create New Product
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Create Dialog */}
      <Dialog
        open={productCreateDialogOpen}
        onClose={handleProductCreateCancel}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            width: '90vw',
            maxWidth: 1200,
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#3498db', color: 'white', fontWeight: 'bold' }}>
          Create New Product
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            <ProductFormEnhanced
              initialData={{
                sku: pendingProductCode,
                name: pendingProductCode,
                status: 'ACTIVE'
              }}
              onSubmit={handleProductCreate}
              onCancel={handleProductCreateCancel}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Add Supplier Dialog - Same as Suppliers.js */}
      <Dialog
        open={addSupplierDialogOpen}
        onClose={() => {
          const user = getCurrentUser();
          const companyId = user?.companyId || user?.company?.id || 1;
          setAddSupplierDialogOpen(false);
          setSupplierFormValues({
            name: '',
            contact_person: '',
            email: '',
            phone: '',
            website: '',
            address: '',
            notes: '',
            company_id: companyId
          });
          setSupplierFormErrors({});
          setSupplierInputValue(''); // Clear input value when dialog closes
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add New Supplier
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={supplierFormValues.name}
              onChange={handleSupplierFormInputChange}
              required
              error={!!supplierFormErrors.name}
              helperText={supplierFormErrors.name}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              fullWidth
              label="Contact Person"
              name="contact_person"
              value={supplierFormValues.contact_person}
              onChange={handleSupplierFormInputChange}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={supplierFormValues.email}
              onChange={handleSupplierFormInputChange}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={supplierFormValues.phone}
              onChange={handleSupplierFormInputChange}
              required
              error={!!supplierFormErrors.phone}
              helperText={supplierFormErrors.phone}
              inputProps={{ maxLength: 15 }}
            />
            <TextField
              fullWidth
              label="Website"
              name="website"
              value={supplierFormValues.website}
              onChange={handleSupplierFormInputChange}
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              fullWidth
              label="Address"
              name="address"
              multiline
              rows={2}
              value={supplierFormValues.address}
              onChange={handleSupplierFormInputChange}
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              fullWidth
              label="Notes"
              name="notes"
              multiline
              rows={2}
              value={supplierFormValues.notes}
              onChange={handleSupplierFormInputChange}
              inputProps={{ maxLength: 255 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => {
            const user = getCurrentUser();
            const companyId = user?.companyId || user?.company?.id || 1;
            setAddSupplierDialogOpen(false);
            setSupplierFormValues({
              name: '',
              contact_person: '',
              email: '',
              phone: '',
              website: '',
              address: '',
              notes: '',
              company_id: companyId
            });
            setSupplierFormErrors({});
          }}>
            Cancel
          </Button>
          <Button onClick={handleSupplierFormSubmit} variant="contained" color="primary">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* No manual bar at bottom, using the Green Button in footer like POS */}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to remove this item?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="primary">
            No
          </Button>
          <Button
            ref={deleteYesRef}
            onClick={() => removeRow(rowToDelete)}
            color="error"
            variant="contained"
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default POKeyboard;
